

import {
  alpha,
  Box,
  Button,
  CircularProgress,
  Container,
  Grid,
  LinearProgress,
  Paper,
  Radio,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { Formik } from 'formik';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { BiCheckCircle } from 'react-icons/bi';
import {
  useFetchThisAssignmentMCQQuery,
  useFetchThisAssignmentMCQResultQuery,
  useSendThisAssignmentAnswerMutation
} from 'redux/reducers/user/dashboard/tms-course';
import MCQResultModal from './MCQResultModal';

const MCQ = () => {
  const theme = useTheme();
  const { id, mid } = useParams();
  const navigate = useNavigate();
  
  const [initialValue, setInitialValue] = useState<any[]>([]);
  const [callResultAPI, setResultAPI] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [quesAnswers, setQuesAnswers] = useState<{ [key: string]: string }>({});

  const { data: MCQData, isLoading: MCQLoading } = useFetchThisAssignmentMCQQuery(id);
  const { mutate: createMcq, isLoading:isSubmitting, isSuccess } = useSendThisAssignmentAnswerMutation();
  
  // const { data: MCQResult } = useFetchThisAssignmentMCQResultQuery(
  //   callResultAPI ? id : null, 
  //   { skip: !callResultAPI }
  // );

    const { data: MCQResult, isLoading: MCQResultLoading } = useFetchThisAssignmentMCQResultQuery(
    callResultAPI === true ? id : null
  );


  useEffect(() => {
    if (MCQData?.multi_choice_question_details) {
      setInitialValue(MCQData.multi_choice_question_details);
    }
  }, [MCQData]);

  useEffect(() => {
    if (isSuccess) {
      setResultAPI(true);
      setOpenModal(true);
    }
  }, [isSuccess]);

  const handleCloseModal = () => {
    setOpenModal(false);
    navigate(`/course/enroll/view/${mid}`);
  };

  const onChangeHandle = (mcqId: string, answerId: string) => {
    setQuesAnswers((prev) => ({ ...prev, [mcqId]: answerId }));
  };

  // Calculate progress percentage
  const totalQuestions = initialValue?.length || 0;
  const answeredCount = Object.keys(quesAnswers).length;
  const progress = totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0;

  if (MCQLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }
  

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <MCQResultModal
        MCQResult={MCQResult}
        handleClose={handleCloseModal}
        openForgetPasswordModal={openModal}
      />

   <Paper
  elevation={0}
  sx={{
    p: 3,
    mb: 4,
    borderRadius: 3,
    bgcolor: alpha(theme.palette.primary.main, 0.05),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    
    // ── Make it sticky ────────────────────────────────
    position: 'sticky',
    top: 70,                     // sticks to the very top
    zIndex: 100,                // stay above question cards
    backdropFilter: 'blur(8px)', // nice modern touch (optional)
    // Optional: slight shadow when stuck
    boxShadow: '0 4px 10px rgba(0,0,0,0.08)',
  }}
>
  <Typography variant="h4" fontWeight="700" color="primary.main" gutterBottom>
    {MCQData?.assignment_title || 'Quiz'}
  </Typography>
  <Typography variant="body2" color="text.secondary" mb={2}>
    Please answer all questions. Your progress is saved automatically as you select options.
  </Typography>
  
  <Box sx={{ mt: 2 }}>
    <Stack direction="row" justifyContent="space-between" mb={1}>
      <Typography variant="caption" fontWeight="bold">
        Progress: {answeredCount} / {totalQuestions} Questions
      </Typography>
      <Typography variant="caption" fontWeight="bold">
        {Math.round(progress)}%
      </Typography>
    </Stack>
    <LinearProgress 
      variant="determinate" 
      value={progress} 
      sx={{ height: 10, borderRadius: 5 }} 
    />
  </Box>
</Paper>

      {initialValue.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center', borderRadius: 4 }}>
          <Typography variant="h6">No questions available for this assignment.</Typography>
        </Paper>
      ) : (
        <Formik
          initialValues={{}}
          onSubmit={() => {
            if (answeredCount < totalQuestions) {
              toast.warn("Please answer all questions before submitting.");
              return;
            }
            const data = Object.values(quesAnswers).map((id) => ({ mcq_answer: id }));
            createMcq({ assignment_id: id, answers: data });
          }}
        >
          {({ handleSubmit }) => (
            <form onSubmit={handleSubmit}>
              {initialValue.map((mcq: any, index: number) => (
                <Paper 
                  key={mcq.mcq_id} 
                  elevation={2} 
                  sx={{ 
                    p: { xs: 2, md: 4 }, 
                    mb: 4, 
                    borderRadius: 4,
                    transition: '0.3s',
                    '&:hover': { boxShadow: theme.shadows[4] }
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="flex-start" mb={3}>
                    <Box 
                      sx={{ 
                        bgcolor: 'primary.main', 
                        color: 'white', 
                        px: 1.5, 
                        py: 0.5, 
                        borderRadius: 1,
                        fontWeight: 'bold',
                        fontSize: '0.875rem'
                      }}
                    >
                      Q{index + 1}
                    </Box>
                    <Typography variant="h6" fontWeight="600" sx={{ lineHeight: 1.4 }}>
                      {mcq.question}
                    </Typography>
                  </Stack>

                  <Grid container spacing={2}>
                    {mcq.options.map((option: any) => {
                      const isSelected = quesAnswers[mcq.mcq_id] === option.mcq_answer_id.toString();
                      return (
                        <Grid item xs={12} sm={6} key={option.mcq_answer_id}>
                          <Box
                            onClick={() => onChangeHandle(mcq.mcq_id, option.mcq_answer_id.toString())}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              p: 2,
                              border: '2px solid',
                              borderColor: isSelected ? 'primary.main' : alpha(theme.palette.divider, 0.1),
                              borderRadius: 3,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease-in-out',
                              bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.05) : 'background.paper',
                              '&:hover': {
                                borderColor: 'primary.light',
                                bgcolor: alpha(theme.palette.primary.main, 0.02),
                                transform: 'translateY(-2px)'
                              }
                            }}
                          >
                            <Radio
                              checked={isSelected}
                              value={option.mcq_answer_id}
                              sx={{ p: 0, mr: 2 }}
                            />
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontWeight: isSelected ? 600 : 400,
                                color: isSelected ? 'primary.dark' : 'text.primary'
                              }}
                            >
                              {option.answer}
                            </Typography>
                          </Box>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              ))}

              <Stack 
                direction="row" 
                justifyContent="center" 
                sx={{ mt: 6, mb: 10 }}
              >
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => handleSubmit()}
                  disabled={isSubmitting}
                  startIcon={isSubmitting ? <CircularProgress size={20} /> : <BiCheckCircle />}
                  sx={{ 
                    px: 8, 
                    py: 1.5, 
                    borderRadius: 10,
                    fontSize: '1.1rem',
                    textTransform: 'none',
                    boxShadow: theme.shadows[8]
                  }}
                >
                  {isSubmitting ? 'Submitting...' : 'Finish & Submit Quiz'}
                </Button>
              </Stack>
            </form>
          )}
        </Formik>
      )}
    </Container>
  );
};

export default MCQ;